import { FormTypes } from "../../../utils/models";
import { Paper } from "@mui/material";
import { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import AboutHelpModal from "./AboutHelpModal";
import { Alert } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import CustomStepper from "../../../components/CustomStepper";
import FormLayout from "../../../components/FormLayout";
import InfoCircleIcon from "@mui/icons-material/InfoRounded";
import LineLoader from "../../../components/Loaders/LineLoader/LineLoader";
import ConfirmScheduleC from "./ConfirmScheduleC";
import { Link } from "react-router-dom";
import { taxSlice } from "../../../store/reducer/tax";
import { goBack } from "redux-first-history";
import {
  ConfirmScheduleCTask,
  DeleteScheduleCTask,
  selectTask,
  UploadScheduleCTask,
} from "../../../utils/tasks";
import { RootState } from "../../../store";
import { User } from "../../../model/user";
import { TaxDocument, TaxDocumentError } from "../../../model/tax/tax_document";
import { IRSScheduleCForm } from "../../../model/tax/irsschedulec";
import FileDropContainer from "../../../components/FileInputWithDragAndDrop/FileDropContainer";
import { IRSSchedule1Form } from "../../../model/tax/irsschedule1";
import { toDollar } from "../../../utils/dataConverter";
import { FileRejection } from "react-dropzone";
import DocumentList from "./DocumentList";
import { ErrorAlert } from "../../../components/Alerts";

const steps = ["upload schedule c", "confirm"];
interface FormsScheduleCProps {
  taxState: RootState["tax"];
  user: User | null;
}

const FormsScheduleC = ({ user, taxState }: FormsScheduleCProps) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  // Note here that we're dealing with multiple forms to edit and toggleEditMode
  const { errorMessage, failureCount } = useSelector(
    (appState: any) => appState.tax
  );

  /*const totalW2 = taxState.irsW2Forms
    .filter((w2) => !(w2 instanceof Error))
    .map((w2) => Number((w2 as TaxDocument<IRSW2Form>).form_data.wages_tips_other_comp_box1))
    .reduce((acc, wages) => acc + wages, 0);*/

  const uploadScheduleCTask = selectTask(taxState, UploadScheduleCTask.id);
  const confirmScheduleCTask = selectTask(taxState, ConfirmScheduleCTask.id);
  const deleteScheduleCTask = selectTask(taxState, DeleteScheduleCTask.id);

  const lastScheduleC =
    taxState.irsScheduleCForms[taxState.irsScheduleCForms.length - 1];

  useEffect(() => {
    dispatch(taxSlice.actions.setEditMode({}));
    if (!taxState.irsSchedule1Forms.length) {
      dispatch(taxSlice.actions.fetchSchedule1FormDetails());
    }
    dispatch(taxSlice.actions.fetchScheduleCFormDetails());
  }, [dispatch]);

  const handleNext = () => {
    if (taxState.irsScheduleCForms.length) {
      dispatch(taxSlice.actions.confirmAllScheduleCRequest());
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const deleteFile = (index: number) => {
    updateEditMode(index)(false);
    dispatch(taxSlice.actions.deleteScheduleCRequest(index));
  };

  const onDropAccepted = (acceptedFiles: any) => {
    dispatch(taxSlice.actions.uploadScheduleCRequest(acceptedFiles[0] as File));
  };

  const [fileDropError, setFileDropError] = useState("");
  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    setFileDropError("Upload failed: " + rejectedFiles[0].errors[0].message);
    setTimeout(() => setFileDropError(""), 4000);
  };

  const updateEditMode = (index: number) => (value: boolean) => {
    dispatch(taxSlice.actions.toggleEditMode({ index, value }));
  };

  const isNextDisabled = () => {
    if (!taxState.irsScheduleCForms.length) {
      return !taxState.uploadedScheduleCs?.length;
    }
    return (
      Object.values(taxState.editMode).includes(true) ||
      taxState.irsScheduleCForms.some((form) => form.document_name === "error")
    );
  };

  const plSch1 = taxState.irsSchedule1Forms
    .filter((sch1) => sch1.document_name !== "error")
    .map((sch1) => (sch1 as TaxDocument<IRSSchedule1Form>).form_data)
    .map((sch1) => Number(sch1.business_income_or_loss ?? 0))
    .reduce((acc, total) => acc + total, 0);

  const plSchC = taxState.irsScheduleCForms
    .filter((schC) => schC.document_name !== "error")
    .map((schC) => (schC as TaxDocument<IRSScheduleCForm>).form_data)
    .map((schC) => Number(schC.net_profit_or_loss ?? 0))
    .reduce((acc, total) => acc + total, 0);

  const plMatches =
    Math.abs(plSch1) + 100 > Math.abs(plSchC) &&
    Math.abs(plSch1) - 100 < Math.abs(plSchC) &&
    plSch1 > 0 === plSchC > 0;

  return (
    <>
      <CustomStepper
        steps={steps}
        activeStep={taxState.irsScheduleCForms.length ? 1 : 0}
        handleNext={handleNext}
        handleBack={() => dispatch(goBack())}
        className="upload-stepper"
        showBackButton={true}
        disableSteps={true}
        nextBtnDisable={isNextDisabled()}
      >
        {!taxState.uploadedScheduleCs ? (
          <LineLoader />
        ) : (
          <FormLayout
            title={"Schedule C Form(s)"}
            subtitle={"File upload"}
            description={
              <span>
                {`It looks like you have some self-employment income. Please upload any Schedule C forms you filed with your Federal return.
              If you have more than one Schedule C, you must upload each one separately.`}
              </span>
            }
            className="upload-form-layout"
            icon={
              <ContactSupportIcon
                titleAccess="Help"
                className={"help-icon"}
                onClick={handleOpen}
              />
            }
          >
            <h5>Documents ({taxState.irsSchedule1Forms.length})</h5>
            <div className="upload-column-wrapper">
              <DocumentList
                documents={taxState.irsScheduleCForms}
                formName="Schedule C"
                title={(document) => document.uploaded_file.file_name}
                onDropAccepted={onDropAccepted}
                properties={[
                  { name: "Profit/loss", prop: "net_profit_or_loss" },
                ]}
                showUpload={!plMatches}
              />
              <div className="activeDocument">
                {taxState.selectedFormIndex === -1 ? (
                  <FileDropContainer
                    uploadTask={uploadScheduleCTask}
                    onDropAccepted={onDropAccepted}
                    onDropRejected={onDropRejected}
                  />
                ) : (
                  <ConfirmScheduleC
                    index={taxState.selectedFormIndex}
                    editMode={true}
                    toggleEditMode={updateEditMode(taxState.selectedFormIndex)}
                    deleteFile={deleteFile}
                    schC={
                      taxState.irsScheduleCForms[
                        taxState.selectedFormIndex
                      ] as TaxDocument<IRSScheduleCForm>
                    }
                  />
                )}
                <div className="mobileDocumentList">
                  {!plMatches && (
                    <FileDropContainer
                      uploadTask={uploadScheduleCTask}
                      onDropAccepted={onDropAccepted}
                      onDropRejected={onDropRejected}
                    />
                  )}
                  {taxState.irsScheduleCForms.map(
                    (
                      fileInfo:
                        | TaxDocument<IRSScheduleCForm>
                        | TaxDocumentError,
                      index: number
                    ) =>
                      fileInfo.document_name === "error" ? (
                        <ErrorAlert>{fileInfo.message}</ErrorAlert>
                      ) : (
                        <ConfirmScheduleC
                          index={index}
                          editMode={true}
                          toggleEditMode={updateEditMode(
                            taxState.selectedFormIndex
                          )}
                          deleteFile={deleteFile}
                          schC={fileInfo}
                        />
                      )
                  )}
                </div>
              </div>
            </div>

            {lastScheduleC && !uploadScheduleCTask && (
              <Alert
                severity={
                  lastScheduleC.document_name === "error" ? "error" : "success"
                }
                variant="outlined"
                classes={{
                  root:
                    lastScheduleC.document_name === "error"
                      ? "error-alert floating-alert"
                      : "success-alert floating-alert",
                  message: "success-msg",
                  icon:
                    lastScheduleC.document_name === "error"
                      ? "error-icon"
                      : "success-icon",
                }}
                iconMapping={{
                  success: <CheckCircleIcon />,
                  error: <InfoCircleIcon />,
                }}
              >
                {!(lastScheduleC.document_name === "error")
                  ? "Your upload was successful."
                  : "Your upload failed, please try again."}
              </Alert>
            )}
            {fileDropError && (
              <ErrorAlert floating>{fileDropError}</ErrorAlert>
            )}
            {taxState.tax_return?.IRSW2.messages?.map((message) => (
              <ErrorAlert>{message}</ErrorAlert>
            ))}
            <Paper elevation={0} className="totalContainer">
              <div className="flex-column">
                <span className="form-static-title">
                  Total Schedule C profit/loss:
                </span>
                <span
                  className={
                    "form-static-value" +
                    " " +
                    (plMatches ? "w-match" : "w-fail")
                  }
                >
                  {toDollar(plSchC)}
                </span>
              </div>
              <div className="flex-column">
                <span className="form-static-title">
                  Profit/loss from Schedule 1:
                </span>
                <span className="form-static-value">{toDollar(plSch1)}</span>
              </div>
              <div className={plMatches ? "w-match" : "w-fail"}>
                {plMatches ? (
                  "Schedule C totals match Schedule 1. Click Continue to proceed"
                ) : (
                  <span>
                    Total profit/loss from all Schedule C's must match Schedule
                    1. Please ensure all fields are correct or{" "}
                    <a
                      href="#"
                      onClick={() => {
                        dispatch(taxSlice.actions.selectForm(-1));
                        if (taxState.selectedFormIndex === -1) {
                          const input = document.createElement("input");
                          input.type = "file";
                          input.onchange = (e) => {
                            const files = (e.target as HTMLInputElement).files;
                            if (files) onDropAccepted(files);
                          };
                          input.click();
                        }
                      }}
                    >
                      upload additional Schedule C's.
                    </a>
                  </span>
                )}
              </div>
            </Paper>
          </FormLayout>
        )}
      </CustomStepper>

      <AboutHelpModal
        open={open}
        formType={FormTypes.scheduleC}
        handleClose={handleClose}
      />
      {(uploadScheduleCTask || confirmScheduleCTask || deleteScheduleCTask) && (
        <LineLoader />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  taxState: state.tax,
  user: state.user.data,
});

export default connect(mapStateToProps)(FormsScheduleC);
